import ApiService from "../../../services/ApiService";
import Logger from "../../../services/Logger";

const PdfService = {
    /**
     * Download a crossword puzzle PDF
     * @param {string} crossId - The crossword puzzle ID
     * @param {string} format - Page format: 'letter' or 'a4' (default 'letter')
     * @param {boolean} [useDisplayClue=false] - When true, request PDF with display-clue wording (REST: useDisplayClue)
     * @returns {Promise<boolean>} - Returns true if download was successful
     */
    downloadCrossword: async (crossId, format = 'letter', useDisplayClue = false) => {
        try {
            if (!crossId) {
                throw new Error('Crossword puzzle ID is required');
            }

            const query = { format, ...(useDisplayClue ? { useDisplayClue: true } : {}) };
            const blob = await ApiService.downloadFile(`/home/cross-play/${crossId}/download-pdf`, query);

            // Create a URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element
            const link = document.createElement('a');
            link.href = url;
            link.download = `puzzle-${crossId}.pdf`; // Set the filename

            // Append to document, click, and cleanup
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Revoke the URL to free up memory
            window.URL.revokeObjectURL(url);

            return true;
        } catch (error) {
            Logger.error('Error downloading crossword puzzle PDF:', error);
            throw error;
        }
    },

    /**
     * Download a grid puzzle PDF
     * @param {string} gridId - The grid puzzle ID
     * @param {string} format - Page format: 'letter' or 'a4' (default 'letter')
     * @param {boolean} [useDisplayClue=false] - When true, request PDF with display-clue wording (REST: useDisplayClue)
     * @returns {Promise<boolean>} - Returns true if download was successful
     */
    downloadGrid: async (gridId, format = 'letter', useDisplayClue = false) => {
        try {
            if (!gridId) {
                throw new Error('Grid puzzle ID is required');
            }

            const query = { format, ...(useDisplayClue ? { useDisplayClue: true } : {}) };
            const blob = await ApiService.downloadFile(`/home/grid-play/${gridId}/download-pdf`, query);

            // Create a URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element
            const link = document.createElement('a');
            link.href = url;
            link.download = `puzzle-${gridId}.pdf`; // Set the filename

            // Append to document, click, and cleanup
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Revoke the URL to free up memory
            window.URL.revokeObjectURL(url);

            return true;
        } catch (error) {
            Logger.error('Error downloading grid puzzle PDF:', error);
            throw error;
        }
    },

    /**
     * Download a puzzle PDF based on puzzle type
     * @param {string} puzzleId - The puzzle ID
     * @param {string} puzzleType - The puzzle type ('CROSSWORD', 'WORD_SEARCH', 'HIDDEN_MESSAGE')
     * @param {string} format - Page format: 'letter' or 'a4' (default 'letter')
     * @param {boolean} [useDisplayClue=false] - When true, pass useDisplayClue to the backend
     * @returns {Promise<boolean>} - Returns true if download was successful
     */
    downloadPuzzle: async (puzzleId, puzzleType, format = 'letter', useDisplayClue = false) => {
        try {
            if (!puzzleId) {
                throw new Error('Puzzle ID is required');
            }

            const isCrossword = puzzleType === 'CROSSWORD';
            
            if (isCrossword) {
                return await PdfService.downloadCrossword(puzzleId, format, useDisplayClue);
            } else {
                return await PdfService.downloadGrid(puzzleId, format, useDisplayClue);
            }
        } catch (error) {
            Logger.error('Error downloading puzzle PDF:', error);
            throw error;
        }
    }
};

export default PdfService;
